import React, { useMemo } from 'react';
import useData from '../../hooks/useData';
import { getDeadlineClass } from '../../utils/helpers';
import { CheckCircleIcon, SunIcon, AddIcon } from '../ui/Icons';
import useToast from '../../hooks/useToast';

const TaskItem = ({ task, onComplete, onTaskClick, onToggleMyDay }) => {
    const { projects } = useData();
    const projectName = useMemo(() => projects.find(p => p.id === task.projectId)?.name, [projects, task.projectId]);
    const today = new Date().toISOString().split('T')[0];
    const isManuallyAdded = task.isMyDay && task.deadline !== today;

    return (
        // --- INÍCIO DA ALTERAÇÃO: Ajustado o estilo para um card com bordas arredondadas ---
        <div 
            onClick={() => onTaskClick(task)}
            className="group p-3 bg-white/5 rounded-xl hover:bg-white/10 transition-all duration-200 cursor-pointer flex justify-between items-center"
        >
        {/* --- FIM DA ALTERAÇÃO --- */}
            <div className="flex items-center gap-3">
                 <button 
                    onClick={(e) => { e.stopPropagation(); onComplete(task); }}
                    title="Marcar como concluída"
                    className="flex-shrink-0 text-gray-400 hover:text-emerald-400 transition-colors"
                >
                    <CheckCircleIcon size={20} />
                </button>
                <div>
                    <p className="text-sm font-medium text-white group-hover:text-indigo-300 transition-colors uppercase">{task.title}</p>
                    <p className="text-xs text-gray-400">{projectName || task.client}</p>
                </div>
            </div>
            <div className="flex items-center gap-2">
                <span className={`text-xs font-semibold ${getDeadlineClass(task.deadline)}`}>
                    {task.deadline ? new Date(task.deadline  + 'T00:00:00').toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit' }) : ''}
                </span>
                {isManuallyAdded && (
                    <button 
                        onClick={(e) => { e.stopPropagation(); onToggleMyDay(task.id, task.isMyDay); }}
                        title="Remover de Meu Dia"
                        className="opacity-50 group-hover:opacity-100 transition-opacity"
                    >
                        <SunIcon size={16} className="text-yellow-400" />
                    </button>
                )}
            </div>
        </div>
    );
};

const SuggestionItem = ({ task, onAdd }) => (
    <div className="p-2 -mx-2 rounded-lg flex justify-between items-center text-sm">
        <div>
            <p className="font-medium text-gray-300 uppercase">{task.title}</p>
            <p className={`text-xs font-semibold ${getDeadlineClass(task.deadline)}`}>
                Vence: {task.deadline ? new Date(task.deadline + 'T00:00:00').toLocaleDateString('pt-BR') : 'N/D'}
            </p>
        </div>
        <button 
            onClick={() => onAdd(task.id, task.isMyDay)}
            title="Adicionar ao Meu Dia"
            className="p-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white"
        >
            <AddIcon size={16} />
        </button>
    </div>
);

const FocusWidget = ({ onTaskClick }) => {
    const { tasks, handleUpdateTask, handleToggleMyDay } = useData();
    const { showToast } = useToast();

    const handleCompleteTask = (task) => {
        const actionText = `marcou a tarefa "${task.title}" como concluída e a arquivou.`;
        handleUpdateTask(task.id, { status: 'Concluído', isArchived: true }, actionText);
        showToast("Tarefa concluída e arquivada!");
    };

    const { myDayTasks, suggestedTasks } = useMemo(() => {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);
        
        const todayString = today.toISOString().split('T')[0];
        const tomorrowString = tomorrow.toISOString().split('T')[0];
        
        const activeTasks = tasks.filter(t => !t.isArchived && t.status !== 'Concluído');

        const myDayList = activeTasks.filter(t => t.isMyDay || t.deadline === todayString);

        const priorityOrder = { 'Alta': 1, 'Média': 2, 'Baixa': 3 };
        myDayList.sort((a, b) => (priorityOrder[a.priority] || 3) - (priorityOrder[b.priority] || 3));
        
        const myDayIds = new Set(myDayList.map(t => t.id));
        const overdue = activeTasks.filter(t => t.deadline && t.deadline < todayString && !myDayIds.has(t.id));
        const oldestOverdue = overdue.sort((a, b) => new Date(a.deadline) - new Date(b.deadline))[0];
        const dueTomorrow = activeTasks.filter(t => t.deadline === tomorrowString && !myDayIds.has(t.id));

        let suggestions = [];
        if (oldestOverdue) suggestions.push(oldestOverdue);
        suggestions = [...suggestions, ...dueTomorrow];

        return { myDayTasks: myDayList, suggestedTasks: suggestions.slice(0, 3) };
    }, [tasks]);

    return (
        <div className="p-4 h-full flex flex-col animate-fade-in overflow-hidden">
            <div className="flex-shrink-0">
                <h3 className="text-lg font-bold text-white">Meu Dia</h3>
                <p className="text-sm text-gray-400 mb-3">{new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
            </div>
            
            <div className="flex-shrink-0 mb-4 p-3 bg-black/20 rounded-xl">
                <h4 className="text-xs font-bold text-indigo-300 uppercase mb-2">Sugestões para Hoje</h4>
                <div className="space-y-1">
                    {suggestedTasks.length > 0 ? (
                        suggestedTasks.map(task => <SuggestionItem key={task.id} task={task} onAdd={handleToggleMyDay} />)
                    ) : (
                        <p className="text-sm text-center text-gray-500 py-2">Nenhuma sugestão por enquanto.</p>
                    )}
                </div>
            </div>

            <div className="space-y-2 overflow-y-auto flex-grow pr-1">
                {myDayTasks.length > 0 ? (
                    myDayTasks.map(task => 
                        <TaskItem 
                            key={task.id} 
                            task={task} 
                            onComplete={handleCompleteTask} 
                            onTaskClick={onTaskClick} 
                            onToggleMyDay={handleToggleMyDay}
                        />)
                ) : (
                    <div className="flex-grow flex items-center justify-center text-center">
                        <div>
                            <p className="text-5xl mb-2">🎉</p>
                            <p className="text-lg font-semibold text-white">Tudo certo por hoje!</p>
                            <p className="text-sm text-gray-400">Você limpou sua lista do dia.</p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FocusWidget;